import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';

import {
  Cached as CachedIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
} from '@frontierag/frontier-ui-library/icons';

import { Alert, Snackbar, Slide } from '@frontierag/frontier-ui-library';
import type { SlideProps } from '@frontierag/frontier-ui-library';

import NotificationContext from './NotificationContext';
import styles from './styles';
import type { TAlertSeverity } from './types';

const AUTO_HIDE_DURATION = 3000;
const VERTICAL_POSITION = 'bottom';
const HORIZONTAL_POSITION = 'right';

const severityIcons = {
  success: <CheckCircleIcon fontSize="inherit" />,
  warning: <WarningIcon fontSize="inherit" />,
  error: <ErrorIcon fontSize="inherit" />,
  info: <InfoIcon fontSize="inherit" />,
};

const customIcons = {
  loading: <CachedIcon sx={styles.loadingIcon} fontSize="inherit" />,
};

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="left" />;
}

export default function NotificationProvider({ children }) {
  const [open, setOpen] = useState<boolean>(false);
  const [message, setMessage] = useState<ReactNode>('');
  const [severity, setSeverity] = useState<TAlertSeverity>('success');
  const [autoHideDuration, setAutoHideDuration] = useState<number>(AUTO_HIDE_DURATION);
  const [customIcon, setCustomIcon] = useState<string>('');

  const customAlertIcon = (customIcon && customIcons[customIcon]) || null;

  const showNotification = useCallback(
    (
      message: ReactNode,
      severity: TAlertSeverity = 'success',
      customIcon = '',
      autoHide: number = AUTO_HIDE_DURATION,
    ) => {
      setOpen(true);
      setMessage(message);
      setSeverity(severity);
      setAutoHideDuration(autoHide);
      setCustomIcon(customIcon);
    },
    [],
  );

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    handleClose();
  };

  const handleClose = () => {
    setOpen(false);
    setMessage('');
  };

  return (
    <>
      <NotificationContext.Provider value={{ notification: showNotification, close: handleClose }}>
        {children}
      </NotificationContext.Provider>
      <Snackbar
        open={open}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: VERTICAL_POSITION, horizontal: HORIZONTAL_POSITION }}
        sx={styles.snackbar}
        TransitionComponent={SlideTransition}
        {...(autoHideDuration && {
          autoHideDuration: Number(autoHideDuration),
        })}
      >
        <Alert
          onClose={handleClose}
          severity={severity}
          iconMapping={severityIcons}
          icon={customAlertIcon}
          variant="filled"
        >
          {message}
        </Alert>
      </Snackbar>
    </>
  );
}
